import { mesh, object, redoPattern, undoPatterns } from './webGL.js';

const THREE = window.THREE;

// Stash the mesh's pre-pattern normal map on the mesh itself (once) and clear
// the live one. The stashed value is used by clearAllPattern to restore the
// original look after the undo/redo history is wiped.
export function captureAndClearNormalMap(targetMesh) {
  if (targetMesh.normalMapOrigin == null) {
    targetMesh.normalMapOrigin = targetMesh.material.normalMap;
  }
  targetMesh.material.normalMap = null;
}

// Restore a previously stashed normal map. No-op if nothing was stashed.
// Force texture re-upload so THREE re-binds it after the material was rendered
// with normalMap === null between capture and restore.
export function restoreNormalMapFromOrigin(targetMesh) {
  if (targetMesh.normalMapOrigin == null) {
    return;
  }
  targetMesh.material.normalMap = targetMesh.normalMapOrigin;
  targetMesh.normalMapOrigin.needsUpdate = true;
  targetMesh.material.needsUpdate = true;
}

window.patternScale = function (scale) {
  let _scale = (scale + 50) / 100;
  if (this.intersection.mesh != null) {
    this.intersection.mesh.material.map.repeat.set(_scale, _scale);
  } else {
    mesh.forEach((m) => {
      m.material.map.repeat.set(_scale, _scale);
    });
  }
};

function addPattern(pattern, intersection, textureCache) {
  // Store current values before loading new texture
  const _currentValue = {
    map: intersection.mesh.material.map,
    normalMap: intersection.mesh.material.normalMap,
    patternFile: intersection.mesh.patternFile,
    updatedTexture: intersection.mesh.updatedTexture,
  };

  const cacheKey = pattern.Thumbnail;
  const cachedTexture = textureCache?.get(cacheKey);

  // If texture is already preloaded and cached, apply it immediately
  if (cachedTexture && cachedTexture.image && cachedTexture.image.complete) {
    const _value = {
      map: cachedTexture,
      normalMap: null,
      patternFile: pattern.file,
      updatedTexture: pattern.Thumbnail,
    };

    if (pattern) {
      const action = {
        type: 'pattern',
        uuid: intersection.mesh.uuid,
        mesh: intersection.mesh,
        value: _value,
        currentValue: _currentValue,
        id: intersection?.mesh?.id,
      };

      undoPatterns.push(action);
      intersection.mesh.material.map = cachedTexture;
      captureAndClearNormalMap(intersection.mesh);
      intersection.mesh.patternFile = pattern.file;
      intersection.mesh.updatedTexture = pattern.Thumbnail;
      intersection.mesh.material.needsUpdate = true;
    }
    redoPattern && (redoPattern.length = 0);
    return;
  }

  // Texture not cached or not ready - load it with callback
  const textureloader = new THREE.TextureLoader();
  textureloader.crossOrigin = 'Anonymous';

  // Load texture with callback - only apply after it's fully loaded
  const temp = textureloader.load(
    pattern.Thumbnail + '?not-from-cache-please',
    (loadedTexture) => {
      // Texture loaded successfully - now apply it
      loadedTexture.wrapS = loadedTexture.wrapT = THREE.RepeatWrapping;
      loadedTexture.repeat.set(8, 8);

      // Cache the loaded texture for future use
      if (textureCache) {
        textureCache.set(cacheKey, loadedTexture);
      }

      const _value = {
        map: loadedTexture,
        normalMap: null,
        patternFile: pattern.file,
        updatedTexture: pattern.Thumbnail,
      };

      if (pattern) {
        const action = {
          type: 'pattern',
          uuid: intersection.mesh.uuid,
          mesh: intersection.mesh,
          value: _value,
          currentValue: _currentValue,
          id: intersection?.mesh?.id,
        };

        undoPatterns.push(action);
        intersection.mesh.material.map = loadedTexture;
        captureAndClearNormalMap(intersection.mesh);
        intersection.mesh.patternFile = pattern.file;
        intersection.mesh.updatedTexture = pattern.Thumbnail;
        intersection.mesh.material.needsUpdate = true;
      }
      redoPattern && (redoPattern.length = 0);
    },
    undefined,
    (error) => {
      console.error('Error loading pattern texture:', error);
      // On error, keep current texture (no blank space)
    },
  );

  // Set wrapping and repeat immediately (texture object exists, just not loaded yet)
  temp.wrapS = temp.wrapT = THREE.RepeatWrapping;
  temp.repeat.set(8, 8);

  //updateProduct();
}

export function doUndoPattern() {
  if (undoPatterns.length > 0) {
    const lastAction = undoPatterns.pop();
    lastAction.mesh.material.map = lastAction.currentValue.map;
    lastAction.mesh.material.normalMap = lastAction.currentValue.normalMap;
    lastAction.mesh.patternFile = lastAction.currentValue.patternFile;
    lastAction.mesh.updatedTexture = lastAction.currentValue.updatedTexture;
    lastAction.mesh.material.needsUpdate = true;
    redoPattern.push(lastAction);
  }
}

export function doRedoPattern() {
  if (redoPattern.length > 0) {
    const lastAction = redoPattern.pop();
    lastAction.mesh.material.map = lastAction.value.map;
    lastAction.mesh.material.normalMap = lastAction.value.normalMap;
    lastAction.mesh.patternFile = lastAction.value.patternFile;
    lastAction.mesh.updatedTexture = lastAction.value.updatedTexture;
    lastAction.mesh.material.needsUpdate = true;
    undoPatterns.push(lastAction);
  }
}

export function clearAllPattern() {
  const THREE = window.THREE;

  if (object) {
    object.traverse((child) => {
      child.patternFile = null;
      child.updatedTexture = null;
    });
  }

  mesh?.forEach(restoreNormalMapFromOrigin);

  if (!window.currentStyle?.main_texture) {
    if (mesh?.length > 0) {
      for (let i = 0; i < mesh.length; i++) {
        // Set material.map and mapOrigin to null to restore blank/white state
        mesh[i].material.map = null;
        mesh[i].mapOrigin = null;
        mesh[i].material.needsUpdate = true;
      }
    }
  }

  if (window.currentStyle?.main_texture) {
    const textureLoader = new THREE.TextureLoader();
    textureLoader.crossOrigin = 'Anonymous';

    const styleTexture = textureLoader.load(
      window.currentStyle.main_texture + '?not-from-cache-please',
      () => {
        if (mesh?.length > 0) {
          styleTexture.wrapS = styleTexture.wrapT = THREE.RepeatWrapping;
          styleTexture.repeat.set(window.currentStyle.Scale || 1, window.currentStyle.Scale || 1);

          for (let i = 0; i < mesh.length; i++) {
            mesh[i].material.map = styleTexture;
            mesh[i].material.needsUpdate = true;
            // Update base texture for future pattern applications
            mesh[i].mapOrigin = styleTexture;
          }
        }
      },
      undefined,
      (error) => {
        console.error('Error loading style texture for pattern reset:', error);
        // If style texture can't be loaded, we already restored to original texture above.
      },
    );

    // Configure immediately (texture object exists, just may not be loaded yet)
    styleTexture.wrapS = styleTexture.wrapT = THREE.RepeatWrapping;
    styleTexture.repeat.set(window.currentStyle.Scale || 1, window.currentStyle.Scale || 1);
  }

  // Clear undo/redo history AFTER restoring (so we can use it for restoration)
  undoPatterns && undoPatterns.splice(0);
  redoPattern && redoPattern.splice(0);
}

export function clearPatternOnly() {
  // Clear only pattern, keep style and other features
  clearAllPattern();
  // Keep window.currentPattern so user can apply pattern again
  // window.currentPattern is already set by patternHandling.selectedPattern()
  if (window.setClearState) {
    window.setClearState(false);
  }
}

class PatternHandling {
  constructor() {
    // Cache for preloaded textures
    this.textureCache = new Map();
    this.preloadPromises = new Map(); // Track loading promises
  }

  /**
   * Preload all pattern textures from patternList
   * @param {Array} patternList - Array of pattern objects with Thumbnail property
   * @param {Function} onProgress - Optional callback for progress (loaded, total)
   * @returns {Promise} Promise that resolves when all textures are loaded
   */
  preloadAllPatterns(patternList, onProgress) {
    if (!patternList || patternList.length === 0) {
      return Promise.resolve();
    }

    const patternsToLoad = patternList.filter(
      (pattern) => pattern && pattern.Thumbnail && !this.textureCache.has(pattern.Thumbnail),
    );

    if (patternsToLoad.length === 0) {
      return Promise.resolve();
    }

    let loadedCount = 0;
    const totalCount = patternsToLoad.length;

    const loadPromises = patternsToLoad.map((pattern) => {
      const cacheKey = pattern.Thumbnail;

      // If already loading, return existing promise
      if (this.preloadPromises.has(cacheKey)) {
        return this.preloadPromises.get(cacheKey);
      }

      const textureloader = new THREE.TextureLoader();
      textureloader.crossOrigin = 'Anonymous';

      const loadPromise = new Promise((resolve, reject) => {
        const texture = textureloader.load(
          cacheKey + '?not-from-cache-please',
          (loadedTexture) => {
            // Texture loaded - configure it
            loadedTexture.wrapS = loadedTexture.wrapT = THREE.RepeatWrapping;
            loadedTexture.repeat.set(8, 8);
            // Cache the loaded texture
            this.textureCache.set(cacheKey, loadedTexture);
            this.preloadPromises.delete(cacheKey);
            loadedCount++;
            if (onProgress) {
              onProgress(loadedCount, totalCount);
            }
            resolve(loadedTexture);
          },
          undefined,
          (error) => {
            console.error('Error preloading pattern texture:', error);
            // Remove from cache and promise map on error
            this.textureCache.delete(cacheKey);
            this.preloadPromises.delete(cacheKey);
            loadedCount++;
            if (onProgress) {
              onProgress(loadedCount, totalCount);
            }
            reject(error);
          },
        );

        // Set wrapping and repeat immediately (texture object exists, just not loaded yet)
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(8, 8);
      });

      this.preloadPromises.set(cacheKey, loadPromise);
      return loadPromise;
    });

    return Promise.allSettled(loadPromises);
  }

  selectedPattern(pattern) {
    window.currentPattern = pattern;

    // Preload texture when pattern is selected for instant application
    if (pattern && pattern.Thumbnail) {
      const cacheKey = pattern.Thumbnail;

      // Only preload if not already cached
      if (!this.textureCache.has(cacheKey)) {
        const textureloader = new THREE.TextureLoader();
        textureloader.crossOrigin = 'Anonymous';

        const texture = textureloader.load(
          cacheKey + '?not-from-cache-please',
          (loadedTexture) => {
            // Texture loaded - configure it
            loadedTexture.wrapS = loadedTexture.wrapT = THREE.RepeatWrapping;
            loadedTexture.repeat.set(8, 8);
            // Cache the loaded texture
            this.textureCache.set(cacheKey, loadedTexture);
          },
          undefined,
          (error) => {
            console.error('Error preloading pattern texture:', error);
            // Remove from cache on error so it can retry
            this.textureCache.delete(cacheKey);
          },
        );

        // Set wrapping and repeat immediately (texture object exists, just not loaded yet)
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(8, 8);
      }
    }
  }

  updatePattern(pattern, intersection) {
    if (intersection.mesh.mapOrigin == null) {
      intersection.mesh.mapOrigin = intersection.mesh.material.map;
    }
    console.log(pattern);
    addPattern(pattern, intersection, this.textureCache);
  }
}

export var patternHandling = new PatternHandling();
